import type { JSX } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';
import type { ValueType } from 'react-select';

import type { Billable, IEvent, ReportModel } from '@feathr/blackbox';
import { AlertV2, Fieldset, ImagePicker, Input } from '@feathr/components';
import { EAlertType } from '@feathr/components/src/AlertV2/AlertV2';
import BillableSelect from '@feathr/extender/components/BillableSelect';

interface IProjectInfoFieldsProps {
  event: ReportModel<IEvent>;
  logoButtonText?: string;
  logoHelpText?: string;
  showLogo?: boolean;
  logoClassName?: string;
}

interface IBillingFieldsProps {
  event: ReportModel<IEvent>;
  billable?: Billable;
  onBillingChange: (selectedBillable?: ValueType<Billable>) => void;
}

/**
 * Shared project information form fields
 * Used by both EventAdd.tsx and CreateFirstProjectModal.tsx
 */
export function ProjectInfoFields({
  event,
  logoButtonText,
  logoHelpText,
  showLogo = true,
  logoClassName,
}: Readonly<IProjectInfoFieldsProps>): JSX.Element {
  const { t } = useTranslation();

  const defaultLogoButtonText = logoButtonText ?? t('Upload project photo');
  const defaultLogoHelpText =
    logoHelpText ??
    t(
      'Upload an image to represent this project. This makes it easier to find this project in your projects list.',
    );

  return (
    <Fieldset>
      <Input
        attribute={'name'}
        label={t('Project name')}
        model={event}
        required={true}
        type={'text'}
      />
      <AlertV2
        description={
          <>
            <p>
              {t(
                'Use descriptive names for easy identification. Include timeframes for recurring initiatives. Keep names concise but specific.',
              )}
            </p>
            <p>
              {t(
                'Examples of good project names: Fall Fundraiser {{year}}, Member Renewal Campaign, Annual Conference {{year}}, New Website Launch.',
                { year: new Date().getFullYear() },
              )}
            </p>
          </>
        }
        title={t('Project Information')}
        type={EAlertType.info}
      />
      {showLogo && (
        <ImagePicker
          attribute={'logo'}
          buttonText={defaultLogoButtonText}
          helpText={defaultLogoHelpText}
          imgClassName={logoClassName}
          label={t('Project logo')}
          model={event}
          optional={true}
          value={event.get('logo')}
        />
      )}
    </Fieldset>
  );
}

/**
 * Shared billing configuration form fields
 * Used by both EventAdd.tsx and CreateFirstProjectModal.tsx
 */
export function BillingFields({
  event,
  billable,
  onBillingChange,
}: Readonly<IBillingFieldsProps>): JSX.Element {
  const { t } = useTranslation();

  return (
    <Fieldset>
      <BillableSelect
        label={t('Billing configuration')}
        onChange={onBillingChange}
        value={billable}
      />
      <Input
        attribute={['billing', 'reference_id']}
        helpText={t(
          'Optional reference identification field (such as a PO number) that will be included on media invoices for this project.',
        )}
        label={t('Reference ID')}
        model={event}
        optional={true}
        type={'text'}
      />
    </Fieldset>
  );
}
